/**
 * Single source of truth for prosjektrolle-rettigheter (VIB-1164 / VIB-1161).
 *
 * Modellen skiller «data/innsyn»-rettigheter (Se/Endre, nivå 0/1/2) fra rene
 * «handlinger» (på/av). Nivåene lagres som nøkkelpar i den eksisterende
 * boolean-matrisen `project_role_permissions` — ingen skjemaendring:
 *
 *   - `edit_X` granted        → nivå 2 (Endre; Se er implisitt)
 *   - kun `view_X` granted    → nivå 1 (Se)
 *   - ingen                   → nivå 0
 *
 * «Endre forutsetter Se» håndheves i resolusjon (`hasPermission`) og i UI.
 * Pure modul, ingen React/Supabase — testbar i isolasjon. UI (fase 3) og
 * resolusjons-hooken (`use-project-permission`) bygger på dette skjemaet.
 */

export const PROJECT_ROLES = [
  { key: "project_lead", label: "Prosjektleder", color: "#031EFF" },
  { key: "client_advisor", label: "Kundekontakt", color: "#50BDD8" },
  { key: "sales", label: "Selger", color: "#BDD95A" },
  { key: "assignee", label: "Tildelt person", color: "#F3A6D8" },
] as const;

export type ProjectRoleKey = (typeof PROJECT_ROLES)[number]["key"];

/** Gruppene «Innsyn & data»-rettigheter vises under på Prosjektroller-fanen. */
export type DataGroup =
  | "Innsyn & data"; // alle data-rettigheter ligger i én bolk i designet

/** Gruppene handlinger vises under. */
export type ActionGroup = "Handlinger";

/**
 * Data/innsyn-rettigheter med Se/Endre-nivå. `viewKey` = Se, `editKey` = Endre.
 * Default-nivåer per rolle (design-startverdier) ligger i DEFAULT_DATA_LEVELS.
 */
export const DATA_RIGHTS = [
  { domain: "economics", label: "Økonomi", viewKey: "view_economics", editKey: "edit_economics" },
  { domain: "internal_notes", label: "Interne notater", viewKey: "view_internal_notes", editKey: "edit_internal_notes" },
  { domain: "contract_amount", label: "Kontraktssum", viewKey: "view_contract_amount", editKey: "edit_contract_amount" },
  { domain: "budget", label: "Timetildeling", viewKey: "view_budget", editKey: "edit_budget" },
  { domain: "invoice_terms", label: "Faktureringsdetaljer", viewKey: "view_invoice_terms", editKey: "edit_invoice_terms" },
  { domain: "team", label: "Team", viewKey: "view_team", editKey: "manage_team" },
  { domain: "documents", label: "Dokumenter", viewKey: "view_documents", editKey: "manage_documents" },
  { domain: "extras", label: "Statister", viewKey: "view_extras", editKey: "manage_extras" },
  { domain: "commission", label: "Provisjonssplitt", viewKey: "view_commission", editKey: "manage_commission_split" },
] as const;

/** Rene handlinger (på/av, ingen Se-variant). */
export const ACTIONS = [
  { key: "approve_hours", label: "Godkjenne timer" },
  { key: "override_task_status", label: "Endre oppgavestatus" },
  { key: "create_offer", label: "Opprette tilbud" },
  { key: "manage_upsells", label: "Administrere mersalg" },
  { key: "create_invoice", label: "Opprette faktura" },
  { key: "approve_vouchers", label: "Godkjenne bilag" },
  { key: "change_phase", label: "Endre fase" },
  { key: "close_project", label: "Avslutte prosjekt" },
  { key: "delete_project", label: "Slette prosjekt" },
] as const;

type DataViewKey = (typeof DATA_RIGHTS)[number]["viewKey"];
type DataEditKey = (typeof DATA_RIGHTS)[number]["editKey"];
type ActionKey = (typeof ACTIONS)[number]["key"];

export type ProjectPermissionKey = DataViewKey | DataEditKey | ActionKey;

/** Alle gyldige permission-nøkler (derivert fra skjemaet). */
export const PROJECT_PERMISSION_KEYS: ProjectPermissionKey[] = [
  ...DATA_RIGHTS.flatMap((d) => [d.viewKey, d.editKey] as ProjectPermissionKey[]),
  ...ACTIONS.map((a) => a.key as ProjectPermissionKey),
];

/** editKey → viewKey (for edit→view-implikasjon). */
export const EDIT_TO_VIEW: Partial<Record<ProjectPermissionKey, ProjectPermissionKey>> =
  Object.fromEntries(DATA_RIGHTS.map((d) => [d.editKey, d.viewKey]));

/** viewKey → editKey. */
export const VIEW_TO_EDIT: Partial<Record<ProjectPermissionKey, ProjectPermissionKey>> =
  Object.fromEntries(DATA_RIGHTS.map((d) => [d.viewKey, d.editKey]));

/**
 * Read-only nøkler som er åpne for alle innloggede ansatte uten prosjektrolle
 * (viewer-floor, VIB-722). Beholdt: se økonomi + se interne notater.
 */
export const VIEWER_FLOOR_KEYS: ProjectPermissionKey[] = [
  "view_economics",
  "view_internal_notes",
];

export type DataLevel = 0 | 1 | 2;

/**
 * Avgjør om en gitt nøkkel er innvilget ut fra et sett med granted-nøkler,
 * med edit→view-implikasjon: en viewKey er innvilget hvis enten viewKey selv
 * eller dens paret editKey er granted.
 */
export function hasPermission(
  granted: ReadonlySet<ProjectPermissionKey> | Set<string>,
  key: ProjectPermissionKey,
): boolean {
  if ((granted as Set<string>).has(key)) return true;
  const editKey = VIEW_TO_EDIT[key];
  return editKey ? (granted as Set<string>).has(editKey) : false;
}

/** Utled Se/Endre-nivå (0/1/2) for en data-rettighet fra granted-settet. */
export function dataLevel(
  granted: ReadonlySet<ProjectPermissionKey> | Set<string>,
  domain: (typeof DATA_RIGHTS)[number]["domain"],
): DataLevel {
  const right = DATA_RIGHTS.find((d) => d.domain === domain);
  if (!right) return 0;
  if ((granted as Set<string>).has(right.editKey)) return 2;
  if ((granted as Set<string>).has(right.viewKey)) return 1;
  return 0;
}
